import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications, useUnreadCount, markAllAsRead } from '../../hooks/useNotifications';
import { groupNotificationsByDate } from '../../types/notifications';
import NotificationItem from './NotificationItem';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { SegmentedControl } from '../ui';
import { Z_INDEX, zStyle } from '../../lib/zLayers';

interface NotificationCenterProps {
  onClose?: () => void;
  /** Bell button (or wrapper) used to position the portaled panel */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose, anchorRef }) => {
  const { colors } = useTheme();
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [showRead, setShowRead] = useState(true);
  const { notifications, isLoading, mutate } = useNotifications({
    type: filterType,
    read: showRead ? undefined : false,
    limit: 50,
  });
  const { count: unreadCount } = useUnreadCount();
  const { showError, showSuccess } = useNotification();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const updatePosition = () => {
    const el = anchorRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    updatePosition();
  }, [anchorRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose?.();
    };

    const onReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [onClose, anchorRef]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      mutate();
      showSuccess('All notifications marked as read');
    } catch (error: any) {
      showError(error?.message || 'Failed to mark all notifications as read');
    }
  };

  const handleMarkAsRead = (_id: number) => {
    mutate();
  };

  const handleDelete = (_id: number) => {
    mutate();
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  const renderGroup = (label: string, items: typeof notifications) => {
    if (!items.length) return null;
    return (
      <div key={label}>
        <div className={cn('flex items-center gap-2.5 px-3.5 py-2 border-b', colors.border, colors.surfaceHover)}>
          <span className={cn('h-px w-3 shrink-0 opacity-80', colors.primary)} aria-hidden />
          <h4 className={cn('text-[10px] font-semibold uppercase tracking-[0.18em]', colors.textMuted)}>
            {label}
          </h4>
        </div>
        {items.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={handleMarkAsRead}
            onDelete={handleDelete}
          />
        ))}
      </div>
    );
  };

  if (!pos) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      role="dialog"
      aria-label="Notifications"
      className={cn(
        'fixed w-[24rem] max-w-[calc(100vw-1rem)] max-h-[min(37.5rem,75vh)] flex flex-col overflow-hidden rounded-xl border shadow-xl',
        colors.surface,
        colors.border,
      )}
      style={{
        top: pos.top,
        right: pos.right,
        ...zStyle(Z_INDEX.portaledDropdown),
      }}
    >
      <div className={cn('px-3.5 py-3 border-b flex items-center justify-between gap-2', colors.border)}>
        <div>
          <h3 className={cn('text-sm font-semibold', colors.text)}>Notifications</h3>
          {unreadCount > 0 && (
            <p className={cn('text-[11px]', colors.textMuted)}>
              {unreadCount} unread
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              className={cn('text-xs font-medium', colors.primaryFg, 'hover:opacity-80')}
            >
              Mark all as read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1 rounded-md transition-colors', colors.textMuted, colors.interactiveHover)}
            aria-label="Close notifications"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className={cn('p-2.5 border-b flex items-center gap-2', colors.border)}>
        <select
          value={filterType || ''}
          onChange={(e) => setFilterType(e.target.value || undefined)}
          className={cn(
            'flex-1 text-sm rounded-lg px-2.5 py-1.5 border focus:outline-none focus:ring-2 focus:ring-amber-700/35',
            colors.border,
            colors.surface,
            colors.text,
          )}
        >
          <option value="">All Types</option>
          <option value="task">Tasks</option>
          <option value="ticket">Tickets</option>
          <option value="reminder">Reminders</option>
          <option value="appointment">Appointments</option>
          <option value="invoice">Invoices</option>
          <option value="client">Clients</option>
        </select>
        <SegmentedControl
          size="sm"
          aria-label="Read filter"
          value={showRead ? 'all' : 'unread'}
          onChange={(v) => setShowRead(v === 'all')}
          options={[
            { id: 'all', label: 'All' },
            { id: 'unread', label: 'Unread', count: unreadCount || undefined },
          ]}
          hideZeroCounts
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className={cn('p-6 text-center text-sm', colors.textMuted)}>Loading notifications…</div>
        ) : notifications.length === 0 ? (
          <div className={cn('p-6 text-center text-sm', colors.textMuted)}>No notifications</div>
        ) : (
          <>
            {renderGroup('Today', groupedNotifications.today)}
            {renderGroup('Yesterday', groupedNotifications.yesterday)}
            {renderGroup('This Week', groupedNotifications.thisWeek)}
            {renderGroup('Older', groupedNotifications.older)}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default NotificationCenter;
